/* eslint-disable @next/next/no-img-element */
import React, { useRef, useState, useEffect } from 'react';
import mergeImages from 'merge-images';
import EmptyState from '../EmptyState';
import clsx from 'clsx';

const positions = {
  1: { x: 375, y: 890 }, // done
  2: { x: 950, y: 554 }, // done
  3: { x: 1530, y: 215 }, // done
  4: { x: 935, y: 1210 }, // done
  5: { x: 1525, y: 890 }, // done
  6: { x: 2100, y: 550 }, // done
  7: { x: 1525, y: 1550 }, // done
  8: { x: 2100, y: 1220 }, // done
  9: { x: 2690, y: 880 }, // done
};

function updateImages(cityBlockIndex, images, block, weight) {
  if (!block) return images.filter((image) => image.cityBlockIndex !== cityBlockIndex);

  const hasBlock = images.find((image) => image.cityBlockIndex === cityBlockIndex);
  const updatedImages = [...images];

  // if block is already in the block, remove it
  if (hasBlock) {
    updatedImages.splice(updatedImages.indexOf(hasBlock), 1);
  }
  if (block) {
    updatedImages.push({ src: `/blocks/builder-client/${block.index}.png`, ...positions[cityBlockIndex], cityBlockIndex, block, weight });
  }

  return updatedImages.sort((a, b) => a.weight - b.weight);
}

async function updateImage(ref, blocks, cityImageUrl) {
  const blockLength = [...blocks].filter((block) => !!block.block).length;

  if (blockLength === 0) return {};

  const updatedImages = blocks.reduce(
    (acc, block) => {
      if (!block) return acc;
      return updateImages(block.index, acc, block.block, block.weight);
    },
    [{ src: cityImageUrl, x: 0, y: 0 }]
  );

  const b64 = await mergeImages(updatedImages, {
    width: 4000,
    height: 2800,
  });

  const canvas = ref.current;
  const ctx = canvas.getContext('2d');
  const image = new Image();
  image.src = b64;

  return { image, ctx, canvas };
}

const CityImage = ({ blocks, imageUrl, cityImageUrl }) => {
  const cityRef = useRef(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (imageUrl) return;

    (async function () {
      const { image, ctx, canvas } = await updateImage(cityRef, blocks, cityImageUrl);
      if (!image) return;

      image.onload = () => {
        // clear ctx
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // draw image
        ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
        setLoading(false);
      };
    })();
  }, [JSON.stringify(blocks), imageUrl, cityImageUrl]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setLoading(false);
    }, 1500);

    return () => clearTimeout(timeout);
  }, []);

  if (imageUrl) {
    return (
      <div className={'aspect-[50/35] z-20 relative w-full'}>
        <div className='absolute z-10 inset-0 w-full h-full flex items-center justify-center'>
          <EmptyState icon='city' message={`Fetching city...`} />
        </div>

        <img
          src={imageUrl}
          alt='Shitty City'
          className={clsx('absolute inset-0 w-full h-full z-20 transition duration-150 rounded-t-lg', loading && 'opacity-0')}
          width={4000}
          height={2800}
        />
      </div>
    );
  }

  return (
    <div className={'aspect-[50/35] relative w-full'}>
      <div className='absolute z-10 inset-0 w-full h-full flex items-center justify-center'>
        <EmptyState icon='city' message={`Fetching city...`} />
      </div>

      {blocks.filter((block) => !!block.block).length > 0 ? (
        <canvas
          ref={cityRef}
          alt='Shitty City base'
          className={clsx('absolute z-20 inset-0 w-full h-full pointer-events-none transition duration-150 rounded-t-lg', loading && 'opacity-0')}
          width={4000}
          height={2800}
        />
      ) : (
        <img
          src={cityImageUrl}
          alt='Shitty City base'
          className={clsx('absolute inset-0 w-full h-full pointer-events-none z-20 transition duration-150 rounded-t-lg', loading && 'opacity-0')}
          width={4000}
          height={2800}
        />
      )}
    </div>
  );
};

export default CityImage;
